import React, { useEffect, useState } from "react";
import useFetch from "hooks/useFetch";
import SectionTabs from "pages/AdminLayout/SectionTabs";
import {
  Alert,
  Badge,
  Card,
  EmptyState,
  Icon,
  IconButton,
  PageHeader,
  Skeleton,
  styles as ui,
  TextField,
  useToast,
} from "stories/Admin";
import ContentModal from "../DuaPage/ContentModal";
import styles from "./NamesOfAllahPage.module.scss";

interface AllahName {
  id: number;
  arabic: string;
  translit: string;
  translation: string;
}

const NamesOfAllahPage: React.FC = () => {
  const { execute, isLoading, error } = useFetch();
  const toast = useToast();
  const [names, setNames] = useState<AllahName[]>([]);
  const [selectedName, setSelectedName] = useState<Omit<
    AllahName,
    "id"
  > | null>(null);
  const [selectedNameId, setSelectedNameId] = useState<number | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchNames = async () => {
      const response = await execute("/api/v1/names/allah", "GET");
      if (response) {
        setNames(response as AllahName[]);
      }
    };
    fetchNames();
  }, []);

  const handleEditClick = (name: AllahName) => {
    const { id, ...editableFields } = name;
    setSelectedName(editableFields);
    setSelectedNameId(id);
    setShowEditModal(true);
  };

  const handleSave = async () => {
    if (!selectedName || selectedNameId === null) return;

    setIsSubmitting(true);
    try {
      const payload = {
        ...selectedName,
        nameType: "ALLAH",
      };

      const response = await execute(
        `/api/v1/names/allah/${selectedNameId}`,
        "PATCH",
        payload,
      );

      if (response) {
        setNames((prevNames) =>
          prevNames.map((n) =>
            n.id === selectedNameId ? { ...n, ...selectedName } : n,
          ),
        );
        setShowEditModal(false);
        toast.success("Navnet er lagret.");
      } else {
        toast.error("Kunne ikke lagre navnet. Prøv igjen.");
      }
    } catch (err) {
      console.error("Error updating name:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Duaer, navn og uttrykk"
        subtitle="Islamsk innhold i appen. Endringer vises med en gang."
      />
      <SectionTabs />

      {isLoading && !showEditModal ? (
        <div className={styles.grid}>
          {Array.from({ length: 8 }, (_, i) => (
            <Card key={i} className={styles.item}>
              <Skeleton width={40} height={20} />
              <Skeleton height={36} />
              <Skeleton width="60%" />
            </Card>
          ))}
        </div>
      ) : error ? (
        <Alert tone="error">
          Kunne ikke hente navnene: {String(error.text)}
        </Alert>
      ) : names.length === 0 ? (
        <EmptyState icon="star" title="Ingen navn">
          Fant ingen av Allahs navn.
        </EmptyState>
      ) : (
        <div className={styles.grid}>
          {names.map((name) => (
            <Card key={name.id} className={styles.item}>
              <div className={styles.itemHead}>
                <Badge tone="grey">
                  <span className={ui.num}>{name.id}</span>
                </Badge>
                <IconButton
                  label="Rediger"
                  icon={<Icon name="edit" size={17} strokeWidth={1.9} />}
                  onClick={() => handleEditClick(name)}
                  style={{ marginLeft: "auto" }}
                />
              </div>
              <p className={`${ui.arabic} ${styles.arabic}`} lang="ar">
                {name.arabic}
              </p>
              <span className={styles.translit}>{name.translit}</span>
              <span className={styles.translation}>{name.translation}</span>
            </Card>
          ))}
        </div>
      )}

      <ContentModal
        open={!!selectedName && showEditModal}
        title="Rediger navn"
        icon="star"
        width={560}
        saving={isSubmitting}
        saveLabel="Lagre"
        onSave={handleSave}
        onClose={() => setShowEditModal(false)}
      >
        {selectedName && (
          <>
            <TextField
              label="Arabisk"
              lang="ar"
              dir="rtl"
              className={styles.arabicInput}
              value={selectedName.arabic}
              onChange={(e) =>
                setSelectedName({ ...selectedName, arabic: e.target.value })
              }
            />
            <TextField
              label="Translitterasjon"
              value={selectedName.translit}
              onChange={(e) =>
                setSelectedName({
                  ...selectedName,
                  translit: e.target.value,
                })
              }
            />
            <TextField
              label="Oversettelse"
              value={selectedName.translation}
              onChange={(e) =>
                setSelectedName({
                  ...selectedName,
                  translation: e.target.value,
                })
              }
            />
          </>
        )}
      </ContentModal>
    </>
  );
};

export default NamesOfAllahPage;
